/**
 * @fileoverview Trigger validation and duplicate detection utilities.
 * Provides functions for detecting identical triggers, wildcard patterns,
 * and managing trigger validation cache for task management.
 * @module helperMethods/task_management/trigger/index
 */

import _ from "lodash";
import store from "@/store";
import moment from "moment";
import { getUserPermissions } from "../index";

/**
 * Fetches identical (duplicate) triggers from the API if cache has expired.
 * Returns cached data if cache is still valid.
 *
 * @returns {Object} Dictionary of identical triggers, or empty object
 *
 * @example
 * const duplicates = getIdenticalTrigger();
 * // Returns cached or fetches duplicate triggers if expired
 */
export function getIdenticalTrigger(): {} {
  // call graphql function
  const identicalTriggerExpiry = _.get(
    store,
    "state.identicalTriggerExpiry",
    moment().subtract(1, "m").format("YYYY-MM-DD HH:mm:ss")
  );
  const currTime = moment();
  if (currTime.isBefore(moment(identicalTriggerExpiry))) {
    store.commit("SET_DUPLICATE_TRIGGERS", { identicalTriggerAPI: [] });
    return [];
  }

  const userHasEditorPermissions = getUserPermissions("editor");

  if (userHasEditorPermissions) {
    store
      .dispatch("GET_DUPLICATE_TRIGGERS")
      .then((duplicateTriggers) => {
        if (duplicateTriggers) {
          return duplicateTriggers;
        }
      })
      .catch((err) => {});
  }
  return {};
}

/**
 * Fetches wildcard triggers from the API if cache has expired.
 * Returns cached data if cache is still valid.
 *
 * @returns {Object} Dictionary of wildcard triggers, or empty object
 *
 * @example
 * const wildcards = getWildCardTrigger();
 * // Returns cached or fetches wildcard triggers if expired
 */
export function getWildCardTrigger(): {} {
  const identicalTriggerExpiry = _.get(
    store,
    "state.identicalTriggerExpiry",
    moment().subtract(1, "m").format("YYYY-MM-DD HH:mm:ss")
  );
  const currTime = moment();
  if (currTime.isBefore(moment(identicalTriggerExpiry))) {
    store.commit("SET_WILDCARD_TRIGGERS", { wildCardTriggerAPI: [] });
    return [];
  }

  const userHasEditorPermissions = getUserPermissions("editor");

  if (userHasEditorPermissions) {
    store
      .dispatch("GET_WILDCARD_TRIGGERS")
      .then((wildcardTriggers) => {
        if (wildcardTriggers) {
          return wildcardTriggers;
        }
      })
      .catch((err) => {});
  }
  return {};
}

/**
 * Checks if a trigger is identical to other triggers based on conditions, context, and pattern/entities.
 * Returns the ID of the first identical trigger found, or empty string if none.
 *
 * @param {string} id - The trigger ID to check
 * @param {Object} changes - Dictionary of trigger groups with identical trigger IDs
 * @returns {string} ID of first identical trigger, or empty string
 *
 * @example
 * const identicalId = checkIdenticalTrigger('trigger123', duplicateGroups);
 * // Returns: 'trigger456' if an identical trigger exists
 */
export function checkIdenticalTrigger(
  id: string,
  changes: { [customIdentifier: string]: { [nodeId: string]: boolean } }
): string {
  const triggerNode = store.state.nodes.trigger[id];
  const triggerType = _.get(triggerNode, "type", "text");
  const conditions = _.get(triggerNode, "conditions", []);
  const entityConditions = _.get(triggerNode, "entityConditions", []);
  const stringifiedConditions = JSON.stringify(conditions);
  const stringifiedEntities = JSON.stringify(entityConditions);
  const context = triggerNode.context || "";
  const pattern = triggerNode.pattern || "";
  const nodeTriggerGroupKey = `${stringifiedConditions},${context},${
    triggerType === "entity" ? stringifiedEntities : pattern
  }`;
  const isAnyNodesIdentical =
    changes[nodeTriggerGroupKey] && Object.keys(changes[nodeTriggerGroupKey]).length > 1;
  if (isAnyNodesIdentical) {
    const otherIdenticalNodes = _.pickBy(changes[nodeTriggerGroupKey], (_, nodeId) => {
      return nodeId !== id;
    });
    if (_.size(otherIdenticalNodes) === 0) {
      return "";
    }
    const firstIdenticalNodeId = _.first(Object.keys(otherIdenticalNodes));
    return firstIdenticalNodeId || "";
  }
  return "";
}

/**
 * Checks if a trigger uses wildcard patterns (|| or |/i).
 * Wildcard patterns can cause performance issues or unintended matches.
 *
 * @param {string} id - The trigger ID to check
 * @returns {boolean} True if the trigger pattern contains wildcard operators
 *
 * @example
 * checkWildCardTrigger('trigger123');
 * // Returns: true if pattern contains '||' or '|/i'
 */
export function checkWildCardTrigger(id: string): boolean {
  const triggerNode = store.state.nodes.trigger[id];
  const isWildcardPattern =
    triggerNode.pattern.includes("||") || triggerNode.pattern.includes("|/i");
  const isInvalid = isWildcardPattern;
  return isInvalid;
}
